import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  type RouterHistory,
} from '@tanstack/react-router'

import { redirectIfSignedIn, requireAuth, requireStaff } from './guards'
import { AppShell } from './layout/app-shell'
import { FormPage } from '@/pages/form'
import { LoginErrorPage } from '@/pages/login-error'
import { ApiDocsDatabasePage } from '@/pages/api-docs-database'
import { ApiDocsPage } from '@/pages/api-docs'
import { DebugPage } from '@/pages/debug'
import { LoginPage } from '@/pages/login'
import { PublicViewPage } from '@/pages/public-view'
import { NotFoundPage } from '@/pages/not-found'
import { SignedOutPage } from '@/pages/signed-out'
import { StyleGuidePage } from '@/pages/style-guide'
import { AdminHealthPage } from '@/pages/admin/health'
import { AdminSettingsPage } from '@/pages/admin/settings'
import { AdminUsersPage } from '@/pages/admin/users'
import { AdminShell } from '@/features/admin/components/admin-shell'
import { DashboardPage, type DashboardSearch } from '@/pages/dashboard'
import { SettingsPage } from '@/pages/settings'
import { TemplatePage } from '@/pages/template'
import { TemplatesPage } from '@/pages/templates'
import { DashboardApplicationPage } from '@/pages/dashboard-app'
import { SyncAssetsChooseDatabasePage, SyncAssetsPage } from '@/pages/sync-assets'
import { TablePage } from '@/pages/table'

const rootRoute = createRootRoute({ component: Outlet })

/**
 * Every id in a path is a database primary key.
 *
 * Returning `null` here becomes `false` at the route, which rejects the match
 * outright. That is better than parsing to `NaN` and letting the page cope:
 * `/workspace/nonsense` is not a workspace this user cannot see, it is not a
 * url this application has, and the two deserve different answers.
 */
function positiveInteger(raw: string): number | null {
  const value = Number(raw)
  return Number.isSafeInteger(value) && value > 0 ? value : null
}

/**
 * `/` is not a page. Where it lands is a question about the session, and the
 * router already knows the answer by the time it is asked.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' })
  },
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Where the user was headed before being sent here, so the sign-in callback can
  // put them back. Validated rather than read raw: it is echoed into a url.
  validateSearch: (search: Record<string, unknown>): { original?: string } => {
    const original = search['original']
    return typeof original === 'string' ? { original } : {}
  },
  beforeLoad: redirectIfSignedIn,
  component: LoginPage,
})

const loginErrorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/error',
  validateSearch: (search: Record<string, unknown>): { error?: string } => {
    const error = search['error']
    return typeof error === 'string' ? { error } : {}
  },
  component: LoginErrorPage,
})

/**
 * A form, as somebody sent a link to it sees it.
 *
 * Outside the shell and outside the guard: a form exists to be filled in by
 * people who have no account here, so requiring one would be requiring an
 * account to answer a question.
 */
const formRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/form/$slug',
  /**
   * The `prefill_` and `hide_` parameters, and nothing else.
   *
   * A pass-through rather than a named list, because the names are the form's
   * own questions and only the form knows them. What it does do is keep
   * everything else out, so a tracking parameter tacked onto a shared link
   * cannot be read as a question, and settle what a repeated parameter means:
   * the last one wins, which is what appending to a link somebody already has
   * amounts to.
   */
  validateSearch: (search: Record<string, unknown>): Record<string, string> => {
    const kept: Record<string, string> = {}
    for (const [key, value] of Object.entries(search)) {
      if (!key.startsWith('prefill_') && !key.startsWith('hide_')) continue
      const last: unknown = Array.isArray(value)
        ? (value as unknown[])[value.length - 1]
        : value
      // A number or a boolean as well as a string, because the router reads
      // `?prefill_Age=30` as a number and every question takes its answer as
      // text. A bare `?hide_Meal` has no value at all and needs none.
      if (typeof last === 'string') kept[key] = last
      else if (typeof last === 'number' || typeof last === 'boolean') {
        kept[key] = String(last)
      } else if (last === null || last === undefined) kept[key] = ''
    }
    return kept
  },
  component: FormPage,
})

/**
 * What this deployment was configured with.
 *
 * Unguarded and outside the shell, because the failure it explains is the one
 * that makes signing in impossible: a page behind the session would be
 * unreachable in exactly the case it is wanted for. Nothing on it is private --
 * every value is already in `/config.js`, which every browser loads.
 */
const debugRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/debug',
  component: DebugPage,
})

/**
 * The design system, as it currently resolves.
 *
 * Unguarded for the same reason `/debug` is, and for one more: it renders
 * nothing that belongs to anybody. It is also what replaces Storybook, which
 * this rewrite drops -- a second build target and a compose service to document
 * primitives shadcn documents upstream, while covering none of the tokens that
 * are the part this fork actually decided.
 */
const styleGuideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/style-guide',
  component: StyleGuidePage,
})

/**
 * The REST reference, and the picker that leads to it.
 *
 * Outside the shell rather than inside it. It is a document read alongside an
 * editor, not a screen of the application, and a sidebar full of tables beside
 * it is the wrong thing to be looking at. It is also unguarded, so the picker
 * can say what it is and offer a way in -- upstream's `/api-docs` does the same,
 * and a sign-in redirect would lose the reason somebody followed the link.
 */
const apiDocsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api-docs',
  component: ApiDocsPage,
})

const apiDocsDatabaseRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/api-docs/database/$databaseId',
  params: {
    parse: ({ databaseId }) => {
      const database = positiveInteger(databaseId)
      return database === null ? false : { databaseId: database }
    },
    stringify: ({ databaseId }) => ({ databaseId: String(databaseId) }),
  },
  component: ApiDocsDatabasePage,
})

/**
 * A grid view, as somebody sent the link sees it.
 *
 * Outside the shell and outside the guard, like the form: the whole point of a
 * shared view is that it can be read by people with no account here.
 *
 * Only a grid. The backend would share any of the seven view types and every
 * view has carried a slug since the moment it was created, but six of them have
 * no page in this build yet -- so `share.ts` offers the switch for a grid and a
 * form and for nothing else. A link nobody can open is not a link.
 */
const publicViewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/public/grid/$slug',
  component: PublicViewPage,
})

const signedOutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/signed-out',
  component: SignedOutPage,
})

/**
 * The signed-in application: the sidebar, the websocket and everything reachable
 * from them.
 *
 * Pathless, so the routes below own the whole url, and guarded once here rather
 * than on each child -- a route added later is protected by where it is declared
 * instead of by remembering to say so.
 *
 * Its `id` does prefix the ids of everything beneath it, which is why a page
 * reads `useParams({ from: '/shell/database/$databaseId/table/$tableId' })` for a
 * url that is `/database/3/table/12`. Ids and paths are different things here;
 * only the path is user-visible.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  beforeLoad: requireAuth,
  component: AppShell,
})

/**
 * The workspace's databases and dashboards. The tab, the search and each filter
 * are in the address; a filter's values are comma-separated, and a default is
 * `undefined` so it never appears there.
 */
const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/dashboard',
  validateSearch: (search: Record<string, unknown>): DashboardSearch => {
    const text = (key: keyof DashboardSearch) => {
      const value = search[key]
      return typeof value === 'string' && value !== '' ? value : undefined
    }
    const tab = search['tab']
    return {
      tab: tab === 'name' || tab === 'owner' ? tab : undefined,
      search: text('search'),
      type: text('type'),
      owner: text('owner'),
      access: text('access'),
      modified: text('modified'),
    }
  },
  component: DashboardPage,
})

const settingsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/settings',
  component: SettingsPage,
})

/**
 * The template gallery. The category pill and the search are in the address, on
 * the same terms as a table's search, and `undefined` is returned explicitly for
 * the same reason.
 */
const templatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/templates',
  validateSearch: (
    search: Record<string, unknown>,
  ): { category?: string | undefined; search?: string | undefined } => {
    const category = search['category']
    const term = search['search']
    return {
      category: typeof category === 'string' && category !== '' ? category : undefined,
      search: typeof term === 'string' && term !== '' ? term : undefined,
    }
  },
  component: TemplatesPage,
})

/** One template, by a platform template's slug or a company template's id. */
const templateRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/templates/$templateId',
  component: TemplatePage,
})

/**
 * The administration area.
 *
 * Guarded here rather than by hiding the link in the user menu, which is not
 * access control: the url still works when typed, and the endpoints beneath it
 * are the ones that would answer. A non-staff account that reaches for any of
 * these pages is sent home before one renders.
 */
const adminRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/admin',
  beforeLoad: requireStaff,
  component: AdminShell,
})

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  beforeLoad: () => {
    // The accounts listing defaults every part of its search, and a redirect
    // has to carry them: the route is typed as requiring them, and arriving
    // without one is what `validateListing` would have to invent a meaning for.
    throw redirect({ to: '/admin/users', search: { page: 1, search: '', sort: '' } })
  },
})

const adminSettingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/settings',
  component: AdminSettingsPage,
})

const adminHealthRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/health',
  component: AdminHealthPage,
})

/**
 * Which page of which search of which order.
 *
 * In the address because it is what is being looked at: an administrator who
 * has found the account they were asked about can send the link, and the back
 * button walks back out of a search rather than out of the page.
 *
 * Every part is defaulted rather than optional, so the pages never have to
 * decide what an absent page number means -- and a nonsense one is replaced
 * with the default rather than passed on.
 */
function validateListing(search: Record<string, unknown>): {
  page: number
  search: string
  sort: string
} {
  const page = Number(search['page'])
  const term = search['search']
  const sort = search['sort']
  return {
    page: Number.isSafeInteger(page) && page > 0 ? page : 1,
    search: typeof term === 'string' ? term : '',
    // Only the shape the endpoint documents: a direction and a field. Anything
    // else is dropped rather than sent, because the endpoint answers a bad sort
    // with a 400 and the whole page would be an error message.
    sort: typeof sort === 'string' && /^[+-][a-z_]+$/.test(sort) ? sort : '',
  }
}

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users',
  validateSearch: validateListing,
  component: AdminUsersPage,
})

const tableRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/database/$databaseId/table/$tableId',
  params: {
    parse: ({ databaseId, tableId }) => {
      const database = positiveInteger(databaseId)
      const table = positiveInteger(tableId)
      return database === null || table === null
        ? false
        : { databaseId: database, tableId: table }
    },
    stringify: ({ databaseId, tableId }) => ({
      databaseId: String(databaseId),
      tableId: String(tableId),
    }),
  },
  /**
   * Which view of the table to show.
   *
   * In the search rather than the path because it is a way of looking at the
   * table rather than a different thing: `?view=` is what makes a link to a
   * filtered, sorted view of a table possible at all, and without it every such
   * link is a link to whichever view happens to sort first.
   *
   * Absent is a real answer and means "whichever comes first", so the address
   * bar stays clean until somebody chooses. A malformed one is dropped rather
   * than rejected -- unlike a path id, which names the thing being looked at and
   * cannot be shrugged off.
   *
   * `undefined` is returned explicitly rather than the key omitted. A route's
   * validated search is merged over the raw one, so omitting the key leaves the
   * raw string in place and the page reads `view=nonsense` as a view id that
   * matches nothing -- "view not found" for a url that names no view at all.
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    view?: number | undefined
    row?: number | undefined
    search?: string | undefined
  } => {
    const view = Number(search['view'])
    const row = Number(search['row'])
    const term = search['search']
    return {
      view: Number.isSafeInteger(view) && view > 0 ? view : undefined,
      // The row opened out, on the same terms as the view: a link to a row is
      // a link somebody sends, and the browser's back button closes the dialog
      // because closing it is where they were a moment ago.
      row: Number.isSafeInteger(row) && row > 0 ? row : undefined,
      // What the reader is looking for, on the same terms again. A search
      // narrows a view to the rows that match, which is a way of looking at
      // the table rather than a preference about it -- so it is in the link,
      // and the browser's back button undoes it.
      //
      // The empty string is dropped rather than kept, so clearing the box
      // leaves the address as clean as it was before anybody typed.
      search: typeof term === 'string' && term !== '' ? term : undefined,
    }
  },
  component: TablePage,
})

function syncAssetsSearch(search: Record<string, unknown>): {
  assetsFileId?: number | undefined
} {
  const file = Number(search['assetsFileId'])
  return { assetsFileId: Number.isSafeInteger(file) && file > 0 ? file : undefined }
}

/**
 * Making a table synced with a spreadsheet in Lithos Assets, in the database
 * chosen. The file is in the search rather than the path: it belongs to another
 * service, and this page is about the database the table goes into.
 */
const syncAssetsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/database/$databaseId/sync/assets',
  params: {
    parse: ({ databaseId }) => {
      const database = positiveInteger(databaseId)
      return database === null ? false : { databaseId: database }
    },
    stringify: ({ databaseId }) => ({ databaseId: String(databaseId) }),
  },
  validateSearch: syncAssetsSearch,
  component: SyncAssetsPage,
})

/**
 * Where Lithos Assets links, naming only the file, since it knows none of the
 * databases: the person picks one first.
 */
const syncAssetsChooseDatabaseRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/sync/assets',
  validateSearch: syncAssetsSearch,
  component: SyncAssetsChooseDatabasePage,
})

const dashboardApplicationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/dashboard/$dashboardId',
  params: {
    parse: ({ dashboardId }) => {
      const id = positiveInteger(dashboardId)
      return id === null ? false : { dashboardId: id }
    },
    stringify: ({ dashboardId }) => ({ dashboardId: String(dashboardId) }),
  },
  /**
   * Which widget's settings are open, on the same terms as an open row: the
   * panel is a place somebody is, so the browser's back button closes it, and a
   * link to a widget somebody is asking about opens the thing being discussed.
   *
   * A malformed one is dropped rather than rejected, and `undefined` is
   * returned explicitly for the reason the table route's is -- a validated
   * search is merged over the raw one, so omitting the key would leave the raw
   * string in place.
   */
  validateSearch: (search: Record<string, unknown>): { widget?: number | undefined } => {
    const widget = Number(search['widget'])
    return { widget: Number.isSafeInteger(widget) && widget > 0 ? widget : undefined }
  },
  component: DashboardApplicationPage,
})

export const routeTree = rootRoute.addChildren([
  indexRoute,
  apiDocsRoute,
  apiDocsDatabaseRoute,
  debugRoute,
  formRoute,
  publicViewRoute,
  loginRoute,
  loginErrorRoute,
  signedOutRoute,
  styleGuideRoute,
  shellRoute.addChildren([
    adminRoute.addChildren([
      adminIndexRoute,
      adminUsersRoute,
      adminSettingsRoute,
      adminHealthRoute,
    ]),
    dashboardRoute,
    dashboardApplicationRoute,
    settingsRoute,
    tableRoute,
    syncAssetsRoute,
    syncAssetsChooseDatabaseRoute,
    templatesRoute,
    templateRoute,
  ]),
])

/**
 * `history` is injectable so a test can start the application on a given url.
 * Nothing in the app passes it -- the browser's history is the default -- but
 * without the seam every route test would have to drive `window.location`.
 */
export function createAppRouter(options: { history?: RouterHistory } = {}) {
  return createRouter({
    routeTree,
    // The session is established before this is called, so a guard never has to
    // wait and there is no in-between state to render.
    defaultPreload: 'intent',
    // Reached by a url that matched no route, and by a route whose `params.parse`
    // rejected what was in the path.
    defaultNotFoundComponent: NotFoundPage,
    ...options,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
